import { FC, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { MAX_ASSESSMENT_TITLE_LENGTH } from '@libs/constants';
import { Trans, useTrans } from '@libs/i18n';

interface EditAssessmentNameModalProps {
  open: boolean;
  currentName: string;
  onClose: () => void;
  onSave: (newName: string) => Promise<void>;
  isLoading?: boolean;
}

/**
 * Modal component for editing assessment name
 * - Validates name (required, max length)
 * - Shows loading state during save
 * - Handles errors
 */
export const EditAssessmentNameModal: FC<EditAssessmentNameModalProps> = observer(({
  open,
  currentName,
  onClose,
  onSave,
  isLoading = false,
}) => {
  const { t: trans } = useTrans();
  const [name, setName] = useState(currentName);
  const [error, setError] = useState<string>('');

  /** Empty string when the name is acceptable. */
  const validate = (value: string): string => {
    if (!value.trim()) return trans('app.assessments.workflow.nameRequired');
    if (value.length > MAX_ASSESSMENT_TITLE_LENGTH) {
      return trans('app.assessments.duplicate.titleTooLong', { max: MAX_ASSESSMENT_TITLE_LENGTH });
    }
    return '';
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newName = event.target.value;
    setName(newName);
    setError(validate(newName));
  };

  const handleSave = async () => {
    const validationError = validate(name);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (name.trim() === currentName.trim()) {
      onClose();
      return;
    }

    try {
      await onSave(name.trim());
      onClose();
    } catch (err) {
      setError('Failed to update assessment name. Please try again.');
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setName(currentName);
      setError('');
      onClose();
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !error && !isLoading) {
      handleSave();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="div">
          <Trans i18nKey="assessment.workflow.editName.title" defaults="Edit Assessment Name" />
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            label={trans('assessment.workflow.editName.label')}
            value={name}
            onChange={handleNameChange}
            onKeyPress={handleKeyPress}
            error={!!error}
            helperText={
              error || `${name.length}/${MAX_ASSESSMENT_TITLE_LENGTH} characters`
            }
            disabled={isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
              },
            }}
          />
        </Box>
      </DialogContent>

      <Button
        onClick={handleClose}
        disabled={isLoading}
        sx={{
          textTransform: 'none',
          color: 'text.secondary',
        }}
      >
        <Trans i18nKey="common.cancelButton" defaults="Cancel" />
      </Button>
      <Button
        onClick={handleSave}
        variant="contained"
        disabled={!!error || !name.trim() || isLoading}
        sx={{
          textTransform: 'none',
          borderRadius: 1.5,
        }}
      >
        <Trans i18nKey="common.saveButton" defaults="Save" />
      </Button>
    </Dialog>
  );
});
